<template>
  <main class="dashboard-page">
    <section class="dashboard-toolbar" aria-label="数据看板控制">
      <p>截至 {{ generatedAt }} · 与前一等长周期比较</p>
      <a-space>
        <a-radio-group :value="store.days" :disabled="store.refreshing" @change="changeDays">
          <a-radio-button v-for="item in dayOptions" :key="item" :value="item"
            >近 {{ item }} 天</a-radio-button
          >
        </a-radio-group>
        <a-button :loading="store.refreshing" @click="store.load()"
          ><i class="ri-refresh-line" />刷新</a-button
        >
      </a-space>
    </section>

    <a-alert
      v-if="store.error"
      class="dashboard-error"
      type="error"
      show-icon
      :message="store.error"
    >
      <template #action><a-button size="small" @click="store.load()">重试</a-button></template>
    </a-alert>

    <a-skeleton v-if="store.loading" active :paragraph="{ rows: 12 }" />
    <template v-else-if="dashboard">
      <section class="kpi-grid" aria-label="核心经营指标">
        <article v-for="item in kpis" :key="item.label">
          <div>
            <span>{{ item.label }}</span
            ><i :class="item.icon" />
          </div>
          <strong>{{ item.value }}</strong>
          <small
            :class="{
              neutral: item.change === null,
              positive: (item.change ?? 0) >= 0,
              negative: (item.change ?? 0) < 0,
            }"
          >
            {{ changeText(item.change)
            }}<template v-if="item.previous !== undefined"> · 上期 {{ item.previous }}</template>
          </small>
        </article>
      </section>

      <section class="dashboard-grid dashboard-grid--top">
        <article class="dashboard-panel trend-panel">
          <header>
            <div>
              <h2>用户增长</h2>
              <p>每日新增注册用户</p>
            </div>
            <strong>{{ formatNumber(dashboard.kpis.newUsers.current) }}</strong>
          </header>
          <DashboardTrendChart
            label="每日新增注册用户趋势"
            :points="registrationTrend"
            color="var(--aw-primary)"
          />
        </article>
        <article class="dashboard-panel composition-panel">
          <header>
            <div>
              <h2>用户结构</h2>
              <p>当前账号与套餐分布</p>
            </div>
          </header>
          <dl class="account-summary">
            <div>
              <dt>用户总数</dt>
              <dd>{{ formatNumber(dashboard.users.total) }}</dd>
            </div>
            <div>
              <dt>有效账号</dt>
              <dd>{{ formatNumber(dashboard.users.enabled) }}</dd>
            </div>
          </dl>
          <div class="plan-list">
            <div v-for="plan in plans" :key="plan.label">
              <span>{{ plan.label }}</span>
              <div><i :style="{ width: `${plan.percent}%` }" /></div>
              <strong>{{ formatNumber(plan.value) }}</strong>
            </div>
          </div>
        </article>
      </section>

      <section class="dashboard-grid dashboard-grid--middle">
        <article class="dashboard-panel trend-panel">
          <header>
            <div>
              <h2>交易趋势</h2>
              <p>每日已支付订单金额</p>
            </div>
            <strong>{{ formatMoney(dashboard.kpis.paidAmountMinor.current) }}</strong>
          </header>
          <DashboardTrendChart label="每日已支付金额趋势" :points="revenueTrend" color="#20bfa9" />
        </article>
        <article class="dashboard-panel commerce-panel">
          <header>
            <div>
              <h2>交易概览</h2>
              <p>当前统计周期</p>
            </div>
          </header>
          <dl class="commerce-summary">
            <div>
              <dt>客单价</dt>
              <dd>{{ formatMoney(dashboard.commerce.averageOrderAmountMinor) }}</dd>
            </div>
            <div>
              <dt>退款金额</dt>
              <dd>{{ formatMoney(dashboard.commerce.refundAmountMinor) }}</dd>
            </div>
            <div>
              <dt>付费用户</dt>
              <dd>{{ formatNumber(dashboard.kpis.paidUsers.current) }}</dd>
            </div>
            <div>
              <dt>已支付订单</dt>
              <dd>{{ formatNumber(dashboard.kpis.paidOrders.current) }}</dd>
            </div>
          </dl>
        </article>
      </section>

      <section class="dashboard-grid dashboard-grid--bottom">
        <article class="dashboard-panel marketing-panel">
          <header>
            <div>
              <h2>营销转化</h2>
              <p>邀请、优惠券与能力商店增长闭环</p>
            </div>
          </header>
          <div class="marketing-funnels">
            <section>
              <h3>邀请增长</h3>
              <dl>
                <div>
                  <dt>邀请注册</dt>
                  <dd>{{ formatNumber(dashboard.marketing.referrals.registered) }}</dd>
                </div>
                <div>
                  <dt>已达标</dt>
                  <dd>{{ formatNumber(dashboard.marketing.referrals.qualified) }}</dd>
                </div>
                <div>
                  <dt>达标率</dt>
                  <dd>{{ percent(dashboard.marketing.referrals.conversionRate) }}</dd>
                </div>
              </dl>
            </section>
            <section>
              <h3>优惠券</h3>
              <dl>
                <div>
                  <dt>领取</dt>
                  <dd>{{ formatNumber(dashboard.marketing.coupons.claimed) }}</dd>
                </div>
                <div>
                  <dt>核销</dt>
                  <dd>{{ formatNumber(dashboard.marketing.coupons.used) }}</dd>
                </div>
                <div>
                  <dt>核销率</dt>
                  <dd>{{ percent(dashboard.marketing.coupons.conversionRate) }}</dd>
                </div>
              </dl>
              <small>进行中活动 {{ dashboard.marketing.coupons.activeCampaigns }} 个</small>
            </section>
            <section>
              <h3>能力商店</h3>
              <dl class="marketplace-funnel">
                <div>
                  <dt>详情浏览</dt>
                  <dd>
                    {{ formatNumber(dashboard.marketing.marketplace.funnel.detail_view ?? 0) }}
                  </dd>
                </div>
                <div>
                  <dt>加入资料库</dt>
                  <dd>
                    {{ formatNumber(dashboard.marketing.marketplace.funnel.library_added ?? 0) }}
                  </dd>
                </div>
                <div>
                  <dt>激活完成</dt>
                  <dd>
                    {{
                      formatNumber(dashboard.marketing.marketplace.funnel.activation_completed ?? 0)
                    }}
                  </dd>
                </div>
              </dl>
            </section>
            <section>
              <h3>官网首个价值</h3>
              <dl>
                <div>
                  <dt>访问 / 注册完成</dt>
                  <dd>{{ formatNumber(dashboard.marketing.website.events.landing_view) }} / {{ formatNumber(dashboard.marketing.website.events.registration_completed) }}</dd>
                </div>
                <div>
                  <dt>首次进入 / 首次运行</dt>
                  <dd>{{ formatNumber(dashboard.marketing.website.events.workspace_first_opened) }} / {{ formatNumber(dashboard.marketing.website.events.first_agent_run_completed) }}</dd>
                </div>
                <div>
                  <dt>访问到注册</dt>
                  <dd>{{ percent(dashboard.marketing.website.conversion.landingToRegistration) }}</dd>
                </div>
              </dl>
            </section>
          </div>
          <div class="top-packages">
            <h3>热门资产</h3>
            <a-empty
              v-if="!dashboard.marketing.marketplace.topPackages.length"
              :image-style="{ height: '34px' }"
              description="本周期暂无资产转化数据"
            />
            <ol v-else>
              <li v-for="item in dashboard.marketing.marketplace.topPackages" :key="item.packageId">
                <span>{{ item.name }}</span
                ><small
                  >激活 {{ formatNumber(item.activationCount) }} · 就绪
                  {{ formatNumber(item.readyCount) }}</small
                >
              </li>
            </ol>
          </div>
        </article>
        <article class="dashboard-panel reminders-panel">
          <header>
            <div>
              <h2>异常提醒</h2>
              <p>只展示需要人工处理的系统事项</p>
            </div>
          </header>
          <div class="reminder-totals">
            <span
              ><b>{{ dashboard.reminders.totals.reviews }}</b
              >审核</span
            ><span
              ><b>{{ dashboard.reminders.totals.refunds }}</b
              >退款</span
            ><span
              ><b>{{ dashboard.reminders.totals.deletions }}</b
              >删除</span
            ><span
              ><b>{{ dashboard.reminders.totals.aiFailures }}</b
              >AI 失败</span
            ><span v-if="session.can('message_review.read')"
              ><b>{{ dashboard.reminders.totals.messageReviews }}</b
              >消息审查</span
            >
          </div>
          <a-empty
            v-if="!dashboard.reminders.items.length"
            :image-style="{ height: '54px' }"
            description="当前没有需要处理的异常"
          />
          <div v-else class="reminder-list">
            <button
              v-for="item in dashboard.reminders.items"
              :key="`${item.kind}:${item.id}`"
              type="button"
              :disabled="!targetFor(item)"
              @click="openReminder(item)"
            >
              <i :class="severityIcon(item.severity)" /><span
                ><strong>{{ item.title }}</strong
                ><small>{{ reminderLabel(item) }} · {{ formatTime(item.updatedAt) }}</small></span
              ><i v-if="targetFor(item)" class="ri-arrow-right-s-line reminder-arrow" />
            </button>
          </div>
        </article>
      </section>
    </template>
  </main>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import type { AdminDashboardReminder, AdminPermission } from 'shared';
import DashboardTrendChart from '@/components/DashboardTrendChart.vue';
import { useAdminDashboardStore } from '@/stores/admin-dashboard.store';
import { useAdminSessionStore } from '@/stores/admin-session.store';

const store = useAdminDashboardStore();
const session = useAdminSessionStore();
const router = useRouter();
const dayOptions = [7, 30, 90] as const;
const dashboard = computed(() => store.dashboard);
let interval: ReturnType<typeof setInterval> | undefined;
const generatedAt = computed(() =>
  dashboard.value ? formatTime(dashboard.value.generatedAt) : '—',
);
const kpis = computed(() =>
  dashboard.value
    ? [
        metric('新增用户', 'ri-user-add-line', dashboard.value.kpis.newUsers),
        metric('付费用户', 'ri-user-star-line', dashboard.value.kpis.paidUsers),
        metric('已支付订单', 'ri-file-list-3-line', dashboard.value.kpis.paidOrders),
        metric('实付金额', 'ri-money-cny-circle-line', dashboard.value.kpis.paidAmountMinor, true),
      ]
    : [],
);
const registrationTrend = computed(
  () =>
    dashboard.value?.trend.map((item) => ({ date: item.date, value: item.registeredUsers })) ?? [],
);
const revenueTrend = computed(
  () =>
    dashboard.value?.trend.map((item) => ({
      date: item.date,
      value: item.paidAmountMinor / 100,
    })) ?? [],
);
const plans = computed(() => {
  const values = dashboard.value?.users.plans ?? { free: 0, pro: 0, team: 0 };
  const total = Math.max(1, values.free + values.pro + values.team);
  return [
    { label: 'Free', value: values.free },
    { label: 'Pro', value: values.pro },
    { label: 'Team', value: values.team },
  ].map((item) => ({ ...item, percent: (item.value / total) * 100 }));
});

function metric(
  label: string,
  icon: string,
  value: { current: number; previous: number; changeRate: number | null },
  money = false,
) {
  return {
    label,
    icon,
    value: money ? formatMoney(value.current) : formatNumber(value.current),
    previous: money ? formatMoney(value.previous) : formatNumber(value.previous),
    change: value.changeRate,
  };
}
function formatNumber(value: number): string {
  return new Intl.NumberFormat('zh-CN').format(value);
}
function formatMoney(value: number): string {
  return new Intl.NumberFormat('zh-CN', {
    style: 'currency',
    currency: 'CNY',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value / 100);
}
function percent(value: number | null): string {
  return value === null ? '—' : `${(value * 100).toFixed(1)}%`;
}
function changeText(value: number | null): string {
  return value === null
    ? '上期为 0'
    : `${value >= 0 ? '↑' : '↓'} ${Math.abs(value * 100).toFixed(1)}%`;
}
function formatTime(value: string): string {
  return new Date(value).toLocaleString('zh-CN', { hour12: false });
}
function changeDays(event: { target: { value: 7 | 30 | 90 } }): void {
  void store.changeDays(event.target.value);
}
function severityIcon(value: AdminDashboardReminder['severity']): string {
  return value === 'critical'
    ? 'ri-alarm-warning-line'
    : value === 'high'
      ? 'ri-error-warning-line'
      : 'ri-information-line';
}
function reminderLabel(item: AdminDashboardReminder): string {
  return item.source.kind === 'marketplace_review'
    ? '资产审核'
    : item.source.kind === 'marketplace_demand_review'
      ? '需求审核'
      : item.source.kind === 'refund'
        ? '退款处理'
        : item.source.kind === 'workspace_deletion'
          ? '工作台删除'
          : item.source.kind === 'message_review'
            ? '消息审查'
          : 'AI 运行异常';
}
function targetFor(
  item: AdminDashboardReminder,
): { name: string; query?: Record<string, string>; permission: AdminPermission } | null {
  const source = item.source.kind;
  const target =
    source === 'refund'
      ? { name: 'admin-refunds', permission: 'commerce.read' as const }
      : source === 'workspace_deletion'
        ? { name: 'admin-workspaces', permission: 'workspace.read' as const }
        : source === 'ai_failure'
          ? { name: 'admin-monitor', query: { taskId: item.source.id } as Record<string, string>, permission: 'dashboard.read' as const }
          : source === 'message_review'
            ? {
                name: 'admin-message-reviews',
                query: { caseId: item.source.id } as Record<string, string>,
                permission: 'message_review.read' as const,
              }
          : {
              name: 'admin-marketplace-reviews',
              query: source === 'marketplace_demand_review' ? { tab: 'demands' } : undefined,
              permission: 'marketplace.review' as const,
            };
  return session.can(target.permission) ? target : null;
}
function openReminder(item: AdminDashboardReminder): void {
  const target = targetFor(item);
  if (target) void router.push(target);
}
function refreshVisible(): void {
  if (document.visibilityState === 'visible') void store.load();
}
onMounted(() => {
  void store.load();
  interval = setInterval(refreshVisible, 300_000);
  window.addEventListener('focus', refreshVisible);
});
onBeforeUnmount(() => {
  if (interval) clearInterval(interval);
  window.removeEventListener('focus', refreshVisible);
});
</script>

<style scoped>
.dashboard-page {
  min-width: 0;
}
.dashboard-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}
.dashboard-toolbar p {
  margin: 0;
  color: var(--aw-text-secondary);
  font-size: 13px;
}
.dashboard-error {
  margin-bottom: 16px;
}
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border: 1px solid var(--aw-border);
  border-radius: var(--aw-radius-md);
  overflow: hidden;
  background: var(--aw-surface);
}
.kpi-grid article {
  min-width: 0;
  padding: 20px;
  border-right: 1px solid var(--aw-border);
}
.kpi-grid article:last-child {
  border: 0;
}
.kpi-grid article > div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--aw-text-secondary);
  font-size: 12px;
}
.kpi-grid i {
  color: var(--aw-primary);
  font-size: 18px;
}
.kpi-grid strong {
  display: block;
  margin: 13px 0 6px;
  color: var(--aw-text);
  font-size: 27px;
  line-height: 1;
  letter-spacing: -0.025em;
  font-variant-numeric: tabular-nums;
}
.kpi-grid small {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.kpi-grid .positive {
  color: #237804;
}
.kpi-grid .negative {
  color: #cf1322;
}
.kpi-grid .neutral {
  color: var(--aw-text-tertiary);
}
.dashboard-grid {
  display: grid;
  gap: 16px;
  margin-top: 16px;
}
.dashboard-grid--top,
.dashboard-grid--middle {
  grid-template-columns: minmax(0, 1.6fr) minmax(290px, 0.8fr);
}
.dashboard-grid--bottom {
  grid-template-columns: minmax(0, 1.6fr) minmax(320px, 0.8fr);
}
.dashboard-panel {
  min-width: 0;
  padding: 20px;
  border: 1px solid var(--aw-border);
  border-radius: var(--aw-radius-md);
  background: var(--aw-surface);
}
.dashboard-panel > header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}
.dashboard-panel h2,
.dashboard-panel h3,
.dashboard-panel p {
  margin: 0;
}
.dashboard-panel h2 {
  color: var(--aw-text);
  font-size: 16px;
}
.dashboard-panel header p {
  margin-top: 5px;
  color: var(--aw-text-secondary);
  font-size: 12px;
}
.dashboard-panel header > strong {
  color: var(--aw-text);
  font-size: 17px;
  font-variant-numeric: tabular-nums;
}
.account-summary,
.commerce-summary {
  display: grid;
  margin: 0;
}
.account-summary {
  grid-template-columns: 1fr 1fr;
  margin-bottom: 24px;
}
.account-summary div:first-child {
  border-right: 1px solid var(--aw-border);
}
.account-summary div {
  padding: 0 16px;
}
.account-summary div:first-child {
  padding-left: 0;
}
.account-summary dt,
.commerce-summary dt,
.marketing-funnels dt {
  color: var(--aw-text-secondary);
  font-size: 12px;
}
.account-summary dd,
.commerce-summary dd,
.marketing-funnels dd {
  margin: 7px 0 0;
  color: var(--aw-text);
  font-size: 21px;
  font-weight: 650;
  font-variant-numeric: tabular-nums;
}
.plan-list {
  display: grid;
  gap: 13px;
}
.plan-list > div {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  color: var(--aw-text-secondary);
  font-size: 12px;
}
.plan-list > div > div {
  height: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: var(--aw-canvas);
}
.plan-list i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--aw-primary);
}
.plan-list strong {
  color: var(--aw-text);
  font-variant-numeric: tabular-nums;
}
.commerce-summary {
  grid-template-columns: 1fr 1fr;
  gap: 18px;
}
.commerce-summary div {
  padding: 15px 0;
  border-top: 1px solid var(--aw-border);
}
.commerce-summary dd {
  font-size: 18px;
}
.marketing-funnels {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border-top: 1px solid var(--aw-border);
  border-bottom: 1px solid var(--aw-border);
}
.marketing-funnels section {
  min-width: 0;
  padding: 16px;
  border-right: 1px solid var(--aw-border);
}
.marketing-funnels section:first-child {
  padding-left: 0;
}
.marketing-funnels section:last-child {
  padding-right: 0;
  border-right: 0;
}
.marketing-funnels h3,
.top-packages h3 {
  color: var(--aw-text);
  font-size: 13px;
}
.marketing-funnels dl {
  display: grid;
  gap: 12px;
  margin: 16px 0 0;
}
.marketing-funnels dd {
  font-size: 16px;
}
.marketing-funnels small {
  display: block;
  margin-top: 14px;
  color: var(--aw-text-tertiary);
  font-size: 11px;
}
.top-packages {
  margin-top: 18px;
}
.top-packages ol {
  display: grid;
  gap: 8px;
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  counter-reset: packages;
}
.top-packages li {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
  color: var(--aw-text);
  font-size: 13px;
}
.top-packages li::before {
  counter-increment: packages;
  content: counter(packages);
  color: var(--aw-text-tertiary);
  font-variant-numeric: tabular-nums;
}
.top-packages li > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.top-packages small {
  color: var(--aw-text-secondary);
  font-size: 11px;
  white-space: nowrap;
}
.reminder-totals {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  margin-bottom: 12px;
  padding: 12px 0;
  border-top: 1px solid var(--aw-border);
  border-bottom: 1px solid var(--aw-border);
}
.reminder-totals span {
  display: grid;
  gap: 3px;
  color: var(--aw-text-secondary);
  font-size: 11px;
  text-align: center;
}
.reminder-totals b {
  color: var(--aw-text);
  font-size: 17px;
  font-variant-numeric: tabular-nums;
}
.reminder-list {
  display: grid;
  gap: 2px;
}
.reminder-list button {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  width: 100%;
  padding: 10px 4px;
  border: 0;
  border-bottom: 1px solid var(--aw-border);
  color: inherit;
  background: transparent;
  text-align: left;
}
.reminder-list button:last-child {
  border-bottom: 0;
}
.reminder-list button:not(:disabled) {
  cursor: pointer;
}
.reminder-list button:not(:disabled):hover strong,
.reminder-list button:not(:disabled):focus-visible strong {
  color: var(--aw-primary);
}
.reminder-list button:focus-visible {
  outline: 2px solid var(--aw-primary);
  outline-offset: 2px;
}
.reminder-list button:disabled {
  cursor: default;
}
.reminder-list > button > i:first-child {
  margin: 2px 8px 0 0;
  color: #d46b08;
  font-size: 16px;
}
.reminder-list span {
  min-width: 0;
}
.reminder-list strong,
.reminder-list small {
  display: block;
}
.reminder-list strong {
  overflow: hidden;
  color: var(--aw-text);
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.reminder-list small {
  margin-top: 4px;
  color: var(--aw-text-tertiary);
  font-size: 11px;
}
.reminder-arrow {
  align-self: center;
  color: var(--aw-text-tertiary);
}
@media (max-width: 1100px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .kpi-grid article:nth-child(2) {
    border-right: 0;
  }
  .kpi-grid article:nth-child(-n + 2) {
    border-bottom: 1px solid var(--aw-border);
  }
  .dashboard-grid--top,
  .dashboard-grid--middle,
  .dashboard-grid--bottom {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 700px) {
  .dashboard-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }
  .dashboard-toolbar :deep(.ant-space) {
    width: 100%;
    justify-content: space-between;
  }
  .kpi-grid article {
    padding: 16px;
  }
  .kpi-grid strong {
    font-size: 23px;
  }
  .marketing-funnels {
    grid-template-columns: 1fr;
  }
  .marketing-funnels section,
  .marketing-funnels section:first-child,
  .marketing-funnels section:last-child {
    padding: 15px 0;
    border-right: 0;
    border-bottom: 1px solid var(--aw-border);
  }
  .marketing-funnels section:last-child {
    border-bottom: 0;
  }
  .dashboard-panel {
    padding: 16px;
  }
}
@media (max-width: 420px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
  .kpi-grid article,
  .kpi-grid article:nth-child(2) {
    border-right: 0;
    border-bottom: 1px solid var(--aw-border);
  }
  .kpi-grid article:last-child {
    border-bottom: 0;
  }
  .account-summary,
  .commerce-summary {
    grid-template-columns: 1fr;
  }
  .account-summary div,
  .account-summary div:first-child {
    padding: 10px 0;
    border-right: 0;
    border-bottom: 1px solid var(--aw-border);
  }
  .account-summary div:last-child {
    border-bottom: 0;
  }
}
</style>
